/**
 * MITLAUFENDER CTA — die Fläche mit Handlungsaufruf, die ab einer bestimmten
 * Slide unten am Bild erscheint und bis zum Ende des Scrollys stehenbleibt.
 *
 * Jans Auftrag 05.08.2026 (Kundenwunsch, mit Benedikt besprochen): „es soll
 * ganz unten irgendwann während des Scrollens (sollte man einstellen können)
 * eine Fläche + Button (HTML/über allem liegend) erscheinen (Ab Slide nr. X bis
 * zum Ende des Scrollys), die einen Text bzw Link enthält."
 *
 * ABGRENZUNG ZUM CTA JE BOX (cta.ts): Der bleibt, wie er ist. Der gehört EINER
 * HTML-Box und scrollt mit ihr vorbei; dieser hier gehört dem ganzen Scrolly
 * und bleibt stehen. Zwei verschiedene Dinge, zwei Ablagen — aber EIN Knopf:
 * der Knopf in der Fläche ist derselbe `CtaConfig`, den auch die Box benutzt,
 * damit man nicht zwei Denkweisen für denselben Knopf lernen muss und wir ihn
 * nicht zweimal pflegen.
 *
 * WARUM DIE REGEL HIER LIEGT und nicht in Editor oder Leser: Der Streifen wird
 * an denselben drei Stellen gezeichnet wie der Box-CTA — Pro-Editor-Bühne,
 * Vorschau, veröffentlichter Scrolly. Jede Stelle, die selbst entscheidet, wann
 * er sichtbar ist, ist eine Stelle, an der die Fassungen auseinanderlaufen.
 */

import type { Color } from "./post.js";
import { CTA_SCHNITT, normalisiereCtaUrl, resolveCta, type CtaConfig, type ResolvedCta } from "./cta.js";
import { grundSchnitt, gueltigerSchnitt } from "./schrift-staerke.js";

/** Anordnung von Zeile und Knopf innerhalb der Fläche. */
export type RunningCtaLayout = "row" | "column";

/**
 * ART des CTA (Jans Ausbau 27.09.2026): LINK = Zeile + Link-Knopf (der
 * ursprüngliche Streifen), VOTE = Abstimmung, RATING = Bewertung,
 * REACTION = kurze Nachricht an den Betreiber (Jan 03.10.2026, gedacht vor
 * allem für die Postkarte). Fehlt die Angabe, ist es ein LINK — so bleibt
 * jeder Altbestand gültig.
 */
export type RunningCtaArt = "link" | "vote" | "rating" | "reaction";

/**
 * Die Arten, bei denen der Leser ANTWORTET (VOTE, RATING, REACTION). Sie
 * stehen auf genau EINER Slide, nie auf der Titelslide, und es soll nur eine
 * je Slide geben — überall dort, wo bisher „VOTE oder RATING" galt.
 */
export const istFrageArt = (art: unknown): art is "vote" | "rating" | "reaction" =>
    art === "vote" || art === "rating" || art === "reaction";

/** Eine Antwort der Abstimmung. Die `id` bleibt fest, auch wenn der Text sich ändert. */
export interface VoteOption {
    id: string;
    text: string;
}

/**
 * Grenzen der Abstimmung. 6 Antworten passen mit Frage auf ein Smartphone,
 * ohne dass das Feld mehr als die halbe Bildhöhe schluckt (≈ 44 px je Zeile).
 */
export const VOTE_MIN_ANTWORTEN = 2;

/**
 * REACTION (Jan 03.10.2026): höchstens 144 Zeichen — ein Emoji zählt als
 * EIN Zeichen, so wie der Leser es sieht. Die Reihe zum Antippen fügt das
 * Emoji ins Feld ein; Text und Emojis lassen sich mischen.
 */
export const REAKTION_MAX_ZEICHEN = 144;
export const REAKTION_EMOJIS = ["\u2764\uFE0F", "\u{1F44D}", "\u{1F44F}", "\u{1F602}", "\u{1F62E}", "\u{1F622}"] as const;

/**
 * Zeichen zählen, wie der Leser sie sieht: „👍🏽" oder „👨‍👩‍👧" ist EIN Zeichen,
 * obwohl es aus mehreren Codepunkten besteht. Ohne `Intl.Segmenter` (ältere
 * Browser) zählen die Codepunkte — dann zählt ein zusammengesetztes Emoji
 * eben mehrfach, nie zu wenig.
 */
export function reaktionZeichen(text: string): number {
    return zeichenListe(text).length;
}

/** Die ersten `max` Zeichen (wie `reaktionZeichen` zählt). */
export function reaktionKuerzen(text: string, max = REAKTION_MAX_ZEICHEN): string {
    const liste = zeichenListe(text);
    return liste.length <= max ? text : liste.slice(0, max).join("");
}

function zeichenListe(text: string): string[] {
    const Segmenter = (Intl as unknown as {
        Segmenter?: new (sprache?: string, optionen?: { granularity: "grapheme" }) => {
            segment: (t: string) => Iterable<{ segment: string }>;
        };
    }).Segmenter;
    if (typeof Segmenter === "function") {
        return Array.from(new Segmenter(undefined, { granularity: "grapheme" }).segment(text), (s) => s.segment);
    }
    return Array.from(text);
}

/**
 * Eine Nachricht so aufbereiten, wie sie gespeichert und gezeigt wird —
 * gleich im Leser (Zähler) und auf dem Server (Prüfung, Speichern):
 *   • Zeilenumbrüche vereinheitlicht, höchstens eine Leerzeile am Stück;
 *   • unsichtbare Steuerzeichen entfernt (auch Richtungswechsel, mit denen
 *     sich Text in der Liste des Betreibers verdreht darstellen ließe) —
 *     NICHT aber die Verbinder, aus denen Emojis bestehen (ZWJ, U+FE0F);
 *   • Leerraum am Anfang und Ende weg.
 */
export function reaktionBereinigen(text: string): string {
    return text
        .replace(/\r\n?/g, "\n")
        .replace(/\t/g, " ")
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u0009\u000B-\u001F\u007F-\u009F]/g, "")
        .replace(/[\u200B\u200E\u200F\u202A-\u202E\u2060\u2066-\u2069\uFEFF]/g, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

/**
 * RATING: Symbol und Grenzen (Jan 27.09.2026: „nur bis maximal 5 Symbole").
 * Daumen statt Kleeblatt seit 07.10.2026 (Jan: „Das Kleeblatt sieht nicht so
 * hübsch aus“) — gespeichertes "clover" zeichnet als Daumen, s. ratingSymbol.
 */
export type RatingSymbol = "star" | "heart" | "thumb";

/** Gespeichertes Symbol → gezeichnetes; Altbestand "clover" wird zum Daumen, Unbekanntes zum Stern. */
export function ratingSymbol(wert: unknown): RatingSymbol {
    if (wert === "heart") return "heart";
    if (wert === "thumb" || wert === "clover") return "thumb";
    return "star";
}
export const RATING_MIN_STUFEN = 3;
export const RATING_MAX_STUFEN = 5;

/**
 * Eine Abgabe bei VOTE, RATING oder REACTION, wie der Viewer sie an die
 * Leserseite meldet (die schickt sie an den Server, Modul src/cta-antworten).
 * Kommt NUR bei echten Lesern: Die Rückmeldung setzt allein die öffentliche
 * Leserseite, und nur ohne `?preview=1`; Editor und alle Vorschauen setzen
 * sie nie.
 */
export interface CtaAbgabe {
    /** `runningCtas[].id` */
    ctaId: string;
    art: "vote" | "rating" | "reaction";
    /**
     * VOTE: gewählte Antwort-ids · RATING: die Zahl als Text, z. B. ["4"] ·
     * REACTION: die Nachricht, `reaktionBereinigen` schon angewandt.
     */
    antwort: string[];
    /** Feste Kennung dieses Geräts für CTAs (localStorage, nicht pro Tab) */
    geraet: string;
}

/** Früheste Slide eines CTA, NULLBASIERT: 1 = „Slide 2" — nie die Titelslide. */
export const ERSTE_CTA_SLIDE = 1;

/**
 * Zeigt der Leser nach der Abgabe das Ergebnis (`showResults`)? Noch NICHT —
 * das kommt mit Stufe 2 (Zählung für Leser). Bis dahin blenden beide Editoren
 * das Häkchen „Leser sehen das Ergebnis“ aus (Jan 07.10.2026: „Häkchen
 * ausblenden, bis es wirkt“). Gespeicherte Werte bleiben erhalten. Auf `true`
 * setzen, sobald RunningVote/RunningRating das Ergebnis zeichnen.
 */
export const ERGEBNIS_FUER_LESER = false;
export const VOTE_MAX_ANTWORTEN = 6;

export interface RunningCtaConfig {
    /** Kennung — nur in der Liste `runningCtas` gesetzt (Pro-Editor, 27.09.2026). */
    id?: string;
    /** Name in der Zeitleiste des Pro-Editors, z. B. „CTA 2". Nur fürs Arbeiten. */
    name?: string;
    /** Hauptschalter. Aus = der Streifen existiert nicht. */
    active?: boolean;
    /**
     * Ab welcher Slide der Streifen erscheint — NULLBASIERT, wie überall im
     * Renderer (`currentSlideIndex`). Die Maske zeigt ihn um 1 erhöht an, weil
     * niemand von „Slide 0" spricht.
     *
     * 0 heißt „von Anfang an". Ein Wert jenseits der letzten Slide heißt
     * faktisch „nie" — das ist erlaubt und kein Fehler, sondern der Weg, den
     * Streifen vorübergehend stillzulegen, ohne die Einstellungen zu verlieren.
     */
    fromSlide?: number;
    /**
     * Bis zu welcher Slide er stehen bleibt — NULLBASIERT und EINSCHLIESSLICH.
     * Fehlt der Wert (oder ist er null), bleibt er bis zum Ende. Kam am
     * 27.09.2026 mit den mehreren CTAs dazu: Wer zwei verschiedene Angebote
     * nacheinander zeigen will, braucht ein Ende für das erste.
     */
    toSlide?: number | null;
    /** Zeile in der Fläche, neben dem Knopf. Leer = nur der Knopf. */
    text?: string;
    /** Schrift der Zeile; leer = Vorgabe des Scrollys. */
    fontFamily?: string;
    /**
     * Schnitt (100 … 900), seit 07.10.2026. LINK: Schnitt der Zeile (fehlt =
     * {@link ZEILE_SCHNITT}). FRAGE: Schnitt des Grundtexts (Antworten,
     * Textfeld) — Frage, gewählte Antwort und Danke bleiben kräftiger, nie
     * dünner (Jans Regel „Grundtext, Fettes bleibt“, s. schrift-staerke.ts);
     * fehlt = {@link frageGrundSchnitt}.
     */
    fontWeight?: number;
    /** Schriftgröße der Zeile in px (bezogen auf die Anzeige, nicht die Bühne). */
    fontSize?: number;
    /** Farbe der Zeile. */
    textColor?: Color | null;
    /** Füllung der Fläche. Ohne sie bliebe die Fläche unsichtbar. */
    fillColor?: Color | null;
    /** Eckenradius der FLÄCHE in px (nicht der des Knopfes). */
    radius?: number;
    /**
     * Volle Breite bis an die Ränder (Vorgabe) oder freischwebende Karte mit
     * Abstand ringsum. Jans Screenshot zeigt die volle Breite; die Karte ist
     * Benedikts Entwurf.
     */
    fullWidth?: boolean;
    /** Zeile und Knopf nebeneinander (Vorgabe) oder untereinander. */
    layout?: RunningCtaLayout;
    /** Schließen-Knopf, mit dem der Leser die Fläche wegräumen kann. */
    dismissible?: boolean;
    /** Der Knopf in der Fläche — dasselbe Bauteil wie in der HTML-Box. */
    cta?: CtaConfig | null;

    /** Art des CTA; fehlt = "link". */
    art?: RunningCtaArt;
    // ── nur VOTE ──
    /** Die Frage über den Antworten. */
    question?: string;
    /** Die Antworten (2–6). */
    options?: VoteOption[];
    /** Mehrere Antworten erlaubt (dann mit Abstimmen-Knopf). */
    multiple?: boolean;
    /** Leser sehen nach der Abgabe das Ergebnis (wirkt ab Stufe 2 mit Zählung). */
    showResults?: boolean;
    /** Satz nach der Abgabe, z. B. „Danke fürs Mitmachen!". */
    thanksText?: string;
    // ── nur REACTION (Aufforderung = question, Danke = thanksText, Senden-Knopf = cta) ──
    /** Grauer Hinweis im leeren Textfeld, z. B. „Deine Nachricht …". */
    placeholder?: string;
    // ── nur RATING (Frage, Danke, Ergebnis-Schalter wie VOTE) ──
    /** Anzahl Stufen, 3–5. */
    scale?: number;
    /** Stern, Herz oder Daumen ("clover" = Altbestand bis 07.10.2026, zeichnet als Daumen). */
    symbol?: RatingSymbol | "clover";
    /** Reihenfolge der Zeilen: "desc" = höchste oben (Vorgabe, Jan 27.09.2026), "asc" = 1 oben. */
    order?: "asc" | "desc";
}

/** Aufbereitete Bewertung. */
export interface ResolvedRating {
    frage: string;
    stufen: number;
    symbol: RatingSymbol;
    /** Zeilen von oben nach unten, z. B. [1,2,3,4,5] oder [5,4,3,2,1]. */
    zeilen: number[];
    ergebnisZeigen: boolean;
    danke: string;
}

/** Gestalt eines Knopfs in einer Frage (dieselben Felder wie der Link-Knopf, ohne Ziel). */
export interface FrageKnopf {
    text: string;
    radius: number;
    borderWidth: number;
    borderColor: Color | null;
    fillColor: Color | null;
    fontFamily: string;
    /** Schnitt der Beschriftung (Vorgabe {@link CTA_SCHNITT}). */
    fontWeight: number;
}

/** Aufbereitete Reaktion: Aufforderung, Textfeld, Senden-Knopf, Danke. */
export interface ResolvedReaction {
    frage: string;
    platzhalter: string;
    danke: string;
    knopf: FrageKnopf;
}

/** Aufbereitete Abstimmung. Der Knopf ist nur bei Mehrfachwahl nötig. */
export interface ResolvedVote {
    frage: string;
    optionen: VoteOption[];
    mehrfach: boolean;
    ergebnisZeigen: boolean;
    danke: string;
    /** Gestalt des Abstimmen-Knopfs (dieselben Felder wie der Link-Knopf, ohne Ziel). */
    knopf: FrageKnopf;
}

/** Aufbereiteter Streifen — oder `null`, wenn keiner gezeigt werden soll. */
export interface ResolvedRunningCta {
    /** Kennung aus der Liste, sonst leer. */
    id: string;
    art: RunningCtaArt;
    /** Nur bei VOTE gesetzt. */
    abstimmung: ResolvedVote | null;
    /** Nur bei RATING gesetzt. */
    bewertung: ResolvedRating | null;
    /** Nur bei REACTION gesetzt. */
    reaktion: ResolvedReaction | null;
    /** Nullbasierter Index der Slide, ab der er erscheint. */
    abSlide: number;
    /** Nullbasierter Index der letzten Slide, auf der er steht; null = bis zum Ende. */
    bisSlide: number | null;
    zeile: string;
    fontFamily: string;
    /** Gewählter Schnitt, `null` = wie bisher (s. {@link RunningCtaConfig.fontWeight}). */
    fontWeight: number | null;
    fontSize: number;
    textColor: Color;
    fillColor: Color;
    radius: number;
    fullWidth: boolean;
    layout: RunningCtaLayout;
    dismissible: boolean;
    /** Der Knopf, schon durch `resolveCta` gegangen — oder `null`. */
    knopf: ResolvedCta | null;
    /**
     * Ziel der ZEILE, wenn es keinen Knopf gibt, aber eine Adresse
     * (Lauras Fund 06.10.2026, WK-Freimarkt: Zeile „Alle Infos" + URL, Knopf
     * ohne Beschriftung → `resolveCta` verwarf den Knopf samt Adresse, der
     * Streifen war nicht klickbar). Dann trägt die Zeile den Link.
     */
    zeilenZiel: { href: string; target: "_blank" | "_self"; rel?: string } | null;
}

/** Schnitt der Zeile einer LINK-Leiste ohne Wahl — SemiBold, wie seit jeher. */
export const ZEILE_SCHNITT = 600;

/**
 * Schnitt des Grundtexts einer FRAGE ohne Wahl, so wie er bisher gezeichnet
 * wird: Antworten der Abstimmung Medium (500), das Textfeld der Reaktion
 * Regular (400). Die Bewertung hat keinen Grundtext (nur Symbole) — Regular.
 */
export function frageGrundSchnitt(art: RunningCtaArt | undefined): number {
    return art === "vote" ? 500 : 400;
}

/** Voreinstellungen — der Streifen soll auf Anhieb lesbar aussehen. */
const VORGABE = {
    fontSize: 16,
    /** Scrolly-Pink, wie der START-Knopf. */
    fill: { r: 240, g: 40, b: 115, a: 1 } as Color,
    text: { r: 255, g: 255, b: 255, a: 1 } as Color,
    radius: 0,
};

const ZAHL = (wert: unknown, ersatz: number): number =>
    typeof wert === "number" && Number.isFinite(wert) ? wert : ersatz;

const TEXT = (wert: unknown): string =>
    typeof wert === "string" ? wert.trim() : "";

/**
 * Den Streifen aus der Dokument-Angabe aufbereiten.
 *
 * `null` heißt: nichts zeichnen. Genau EINE Regel dafür, damit Editor und Leser
 * nicht unterschiedlich entscheiden:
 *
 *   - Ausgeschaltet → nichts.
 *   - Weder eine Zeile NOCH ein brauchbarer Knopf → nichts. Eine leere farbige
 *     Fläche unten im Bild wäre kein Angebot, sondern ein Fehler, den niemand
 *     als solchen erkennt.
 *
 * Ob der Knopf brauchbar ist, entscheidet `resolveCta` — dieselbe Regel wie in
 * der HTML-Box, inklusive „ohne Zieladresse wird nichts gezeichnet".
 *
 * NICHT hier entschieden wird, ob der Streifen GERADE sichtbar ist: das hängt
 * an der Slide, auf der der Leser steht, und steht in `runningCtaSichtbar`.
 */
export function resolveRunningCta(
    cfg: RunningCtaConfig | null | undefined,
): ResolvedRunningCta | null {
    if (!cfg || !cfg.active) return null;

    const art: RunningCtaArt = istFrageArt(cfg.art) ? cfg.art : "link";
    const abstimmung = art === "vote" ? resolveVote(cfg) : null;
    const bewertung = art === "rating" ? resolveRating(cfg) : null;
    const reaktion = art === "reaction" ? resolveReaction(cfg) : null;
    const zeile = art === "link" ? TEXT(cfg.text) : "";
    const knopf = art === "link" ? resolveCta(cfg.cta) : null;
    const zeilenHref = art === "link" && zeile && !knopf ? normalisiereCtaUrl(TEXT(cfg.cta?.url)) : "";
    const zeilenTarget = cfg.cta?.target === "self" ? "_self" : "_blank";
    // Bewertung und Reaktion sind immer vollständig (die Frage darf fehlen)
    if (art === "vote" ? !abstimmung : art === "link" ? !zeile && !knopf : false) return null;

    // Eine FRAGE (VOTE/RATING/REACTION) gilt immer für genau EINE Slide (Jan
    // 27.09.2026) — ihr Ende ist ihr Anfang, egal was gespeichert ist.
    const bis = art === "link" ? cfg.toSlide : null;
    const nurEineSlide = art !== "link";
    return {
        id: TEXT(cfg.id),
        art,
        abstimmung,
        bewertung,
        reaktion,
        bisSlide: nurEineSlide
            ? Math.max(ERSTE_CTA_SLIDE, Math.round(ZAHL(cfg.fromSlide, ERSTE_CTA_SLIDE)))
            : typeof bis === "number" && Number.isFinite(bis)
              ? Math.max(0, Math.round(bis))
              : null,
        // NIE auf der Titelslide (Jans Regel 27.09.2026: „Einen CTA auf dem
        // 1. Slide/Titelslide darf es nicht geben") — frühestens Slide 2,
        // nullbasiert also 1. Gilt auch für Altbestand mit fromSlide 0.
        abSlide: Math.max(ERSTE_CTA_SLIDE, Math.round(ZAHL(cfg.fromSlide, ERSTE_CTA_SLIDE))),
        zeile,
        fontFamily: TEXT(cfg.fontFamily),
        fontWeight: gueltigerSchnitt(cfg.fontWeight) ?? null,
        fontSize: ZAHL(cfg.fontSize, VORGABE.fontSize),
        textColor: cfg.textColor ?? VORGABE.text,
        fillColor: cfg.fillColor ?? VORGABE.fill,
        radius: Math.max(0, ZAHL(cfg.radius, VORGABE.radius)),
        // Vorgabe ist die volle Breite (Jans Screenshot).
        fullWidth: cfg.fullWidth !== false,
        layout: cfg.layout === "column" ? "column" : "row",
        dismissible: cfg.dismissible === true,
        knopf,
        zeilenZiel: zeilenHref
            ? {
                  href: zeilenHref,
                  target: zeilenTarget,
                  rel: zeilenTarget === "_blank" ? "noopener noreferrer" : undefined,
              }
            : null,
    };
}

/**
 * Abstimmung aufbereiten — `null`, solange weniger als 2 Antworten Text haben
 * (eine Abstimmung mit einer Antwort ist keine). Mehr als 6 werden gekappt.
 */
export function resolveVote(cfg: RunningCtaConfig): ResolvedVote | null {
    const optionen = (Array.isArray(cfg.options) ? cfg.options : [])
        .map((o) => ({ id: TEXT(o?.id), text: TEXT(o?.text) }))
        .filter((o) => o.id && o.text)
        .slice(0, VOTE_MAX_ANTWORTEN);
    if (optionen.length < VOTE_MIN_ANTWORTEN) return null;
    return {
        frage: TEXT(cfg.question),
        optionen,
        mehrfach: cfg.multiple === true,
        ergebnisZeigen: cfg.showResults === true,
        danke: TEXT(cfg.thanksText),
        knopf: frageKnopf(cfg, "Abstimmen"),
    };
}

/** Knopf einer Frage aus dem `cta`-Feld — Vorgaben wie der Abstimmen-Knopf. */
function frageKnopf(cfg: RunningCtaConfig, text: string): FrageKnopf {
    const k = cfg.cta ?? {};
    return {
        text: TEXT(k.text) || text,
        radius: Math.max(0, ZAHL(k.radius, 100)),
        // Vorgabe 2 px wie alle Knöpfe (Jans Vorgabe 03.10.2026, s. cta.ts).
        borderWidth: Math.max(0, ZAHL(k.borderWidth, 2)),
        borderColor: k.borderColor ?? null,
        fillColor: k.fillColor ?? null,
        fontFamily: TEXT(k.fontFamily),
        fontWeight: grundSchnitt(k.fontWeight, CTA_SCHNITT),
    };
}

/** Reaktion aufbereiten — immer zeichenbar; leere Texte bekommen im Bauteil ihre Vorgabe. */
export function resolveReaction(cfg: RunningCtaConfig): ResolvedReaction {
    return {
        frage: TEXT(cfg.question),
        platzhalter: TEXT(cfg.placeholder),
        danke: TEXT(cfg.thanksText),
        knopf: frageKnopf(cfg, "Senden"),
    };
}

/** Bewertung aufbereiten: 3–5 Stufen (Vorgabe 5), Stern als Vorgabe-Symbol. */
export function resolveRating(cfg: RunningCtaConfig): ResolvedRating {
    const stufen = Math.min(
        RATING_MAX_STUFEN,
        Math.max(RATING_MIN_STUFEN, Math.round(ZAHL(cfg.scale, RATING_MAX_STUFEN))),
    );
    const zeilen = Array.from({ length: stufen }, (_, i) => i + 1);
    return {
        frage: TEXT(cfg.question),
        stufen,
        symbol: ratingSymbol(cfg.symbol),
        // Vorgabe: 5 oben, 1 unten (Jans Korrektur 27.09.2026)
        zeilen: cfg.order === "asc" ? zeilen : zeilen.reverse(),
        ergebnisZeigen: cfg.showResults === true,
        danke: TEXT(cfg.thanksText),
    };
}

/**
 * Steht der Leser in der Spanne des Streifens?
 *
 * Bis 27.09.2026 galt „ab Slide X bis zum Ende" ohne Ende-Feld. Mit mehreren
 * CTAs (Jans Umbau) bekam jeder ein optionales Ende; fehlt es, bleibt es beim
 * alten „bis zum Ende".
 */
export function runningCtaSichtbar(
    streifen: ResolvedRunningCta | null,
    slideIndex: number,
): boolean {
    if (!streifen) return false;
    if (slideIndex < streifen.abSlide) return false;
    return streifen.bisSlide === null || slideIndex <= streifen.bisSlide;
}

/**
 * Steht am ENDE des Scrollys ein CTA — also auf seiner letzten Slide? Dort
 * bekommt der Leser im Viewer Nachlauf, bevor der Footer kommt
 * (VisualStoryPlayer, Jans prod-Test 03.10.2026 mit der Postkarte).
 */
export function ctaAmEnde(streifen: readonly ResolvedRunningCta[], slideCount: number): boolean {
    return slideCount > 0 && streifen.some((s) => runningCtaSichtbar(s, slideCount - 1));
}

/**
 * AUFTRITT eines CTA, am SCROLLEN festgemacht statt an der Uhr (Jans
 * iPhone-Funde 27.09.2026). Vorher glitt der CTA mit fester Dauer (1,4 s)
 * herein, ausgelöst von einem Slide-Wechsel — mal kam er zu früh, mal (per
 * NAV-Knopf) stand das Bild schon und der CTA kam hinterher. Jetzt bewegt er
 * sich mit der Zeit des Scrollys, wie das Bild selbst:
 *
 *   HEREIN   auf dem letzten Stück (CTA_HEREIN) des Wegs zu seiner ersten
 *            Slide — bei Ankunft ist er ganz da.
 *   STEHEN   auf seinen Slides und noch CTA_STEHEN des Wegs zur nächsten.
 *   HINAUS   danach auf CTA_HEREIN des Wegs. Wartet auf der nächsten Slide
 *            schon ein CTA, geht der alte genau dann, wenn der neue kommt
 *            (direkter Wechsel ohne Lücke).
 *
 * `anteil` 0…1 (0 = draußen, 1 = ganz da), `geht` = in der HINAUS-Phase (der
 * Stapel legt ihn dann unter den kommenden). Rückwärts läuft alles gespiegelt.
 * `zeiten` = Anfangszeit jeder Slide (SlidePlayer.slideTimings).
 *
 * STEHEN 0,6 statt 0,4 (Jans prod-Test 03.10.2026: „ein kleines wenig länger
 * stehen bleiben"): ganz da bis 60 % des Wegs zur nächsten Slide, draußen bei
 * 95 % — also immer noch weg, bevor die nächste Slide ankommt.
 */
export const CTA_HEREIN = 0.35;
export const CTA_STEHEN = 0.6;

export function ctaAuftritt(
    streifen: ResolvedRunningCta,
    alle: readonly ResolvedRunningCta[],
    zeit: number,
    zeiten: readonly number[],
): { anteil: number; geht: boolean } {
    const klemme = (x: number) => Math.min(1, Math.max(0, x));
    const letzte = zeiten.length - 1;
    const ab = Math.min(streifen.abSlide, letzte);

    // HEREIN
    let herein = 1;
    if (ab > 0) {
        const weg = CTA_HEREIN * (zeiten[ab] - zeiten[ab - 1]);
        herein = weg > 0 ? klemme((zeit - (zeiten[ab] - weg)) / weg) : zeit >= zeiten[ab] ? 1 : 0;
    }

    // HINAUS (nur mit Ende vor der letzten Slide)
    const bis = streifen.bisSlide;
    if (bis === null || bis >= letzte) return { anteil: herein, geht: false };
    const strecke = zeiten[bis + 1] - zeiten[bis];
    const weg = CTA_HEREIN * strecke;
    const nachfolger = alle.some((s) => s !== streifen && s.abSlide === bis + 1);
    const start = nachfolger ? zeiten[bis + 1] - weg : zeiten[bis] + CTA_STEHEN * strecke;
    const hinaus = weg > 0 ? 1 - klemme((zeit - start) / weg) : zeit > start ? 0 : 1;
    return { anteil: Math.min(herein, hinaus), geht: zeit > start };
}

/**
 * Welche FRAGEN (VOTE/RATING/REACTION) überschneiden sich zeitlich? (Jan 27.09.2026:
 * zwei Fragen gleichzeitig sind auf dem Handy zu viel.) Liefert je Kennung die
 * Namen der CTAs, mit denen sie sich Slides teilt. Links zählen nicht mit —
 * eine Link-Leiste darf neben allem stehen. Nur eingeschaltete zählen.
 */
export function ueberschneidendeFragen(
    liste: RunningCtaConfig[],
    slideCount: number,
): Map<string, string[]> {
    const letzte = Math.max(ERSTE_CTA_SLIDE, slideCount - 1);
    const fragen = liste
        .filter((c) => c.active && istFrageArt(c.art) && c.id)
        .map((c) => {
            const von = Math.max(ERSTE_CTA_SLIDE, Math.round(ZAHL(c.fromSlide, ERSTE_CTA_SLIDE)));
            // Fragen gelten nur für EINE Slide (s. resolveRunningCta)
            return { id: c.id as string, name: TEXT(c.name) || "CTA", von, bis: von };
        })
        // hinter der letzten Slide erscheint sie nie → kann nichts verdecken
        .filter((f) => f.von <= letzte);
    const ergebnis = new Map<string, string[]>();
    for (const a of fragen) {
        const mit = fragen.filter((b) => b.id !== a.id && a.von <= b.bis && b.von <= a.bis).map((b) => b.name);
        if (mit.length > 0) ergebnis.set(a.id, mit);
    }
    return ergebnis;
}

/**
 * ALLE Streifen eines Scrollys, in Reihenfolge: zuerst die Liste
 * `runningCtas` (Pro-Editor ab 27.09.2026), dann der einzelne Altbestand
 * `runningCta` (ältere Scrollys, Template-Editor). Der Pro-Editor zieht den
 * Altbestand beim Öffnen in die Liste um, deshalb kommt keiner doppelt.
 */
export function alleRunningCtas(
    presOpts:
        | {
              runningCta?: RunningCtaConfig | null;
              running_cta?: RunningCtaConfig | null;
              runningCtas?: RunningCtaConfig[] | null;
          }
        | null
        | undefined,
): ResolvedRunningCta[] {
    if (!presOpts) return [];
    const liste = Array.isArray(presOpts.runningCtas) ? presOpts.runningCtas : [];
    const einzeln = presOpts.runningCta ?? presOpts.running_cta ?? null;
    return [...liste, ...(einzeln ? [einzeln] : [])]
        .map((cfg) => resolveRunningCta(cfg))
        .filter((s): s is ResolvedRunningCta => s !== null);
}

/**
 * Ist der Streifen zwar eingeschaltet, aber noch unbrauchbar? Dann ist er
 * ABSICHTLICH angelegt und nur unvollständig — die Maske zeigt dafür einen
 * Hinweis, statt ihn kommentarlos verschwinden zu lassen. Gleiche Idee wie
 * `ctaZielFehlt` beim Box-Knopf.
 */
export function runningCtaUnvollstaendig(
    cfg: RunningCtaConfig | null | undefined,
): boolean {
    if (!cfg || !cfg.active) return false;
    return resolveRunningCta(cfg) === null;
}
